import * as React from 'react';
import type {
  AgentClarificationAnswerBundle,
  AgentClarificationGroup
} from '@cdf/api-client-react';
import { SendIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

type AnswerDraft = {
  optionIds: string[];
  text: string;
};

export interface AgentClarificationQuestionsProps {
  group: AgentClarificationGroup;
  disabled?: boolean;
  onSubmit: (bundle: AgentClarificationAnswerBundle, summary: string) => void;
}

function initialDrafts(group: AgentClarificationGroup): Record<string, AnswerDraft> {
  return Object.fromEntries(group.questions.map((question) => [question.id, { optionIds: [], text: '' }]));
}

export function formatClarificationAnswerSummary(
  group: AgentClarificationGroup,
  bundle: AgentClarificationAnswerBundle
): string {
  const answers = new Map(bundle.answers.map((answer) => [answer.questionId, answer]));
  return [
    'Clarification answers',
    ...group.questions.map((question) => {
      const answer = answers.get(question.id);
      const optionById = new Map(question.options.map((option) => [option.id, option.label]));
      const values = answer?.optionIds.flatMap((id) => optionById.get(id) ? [optionById.get(id)!] : []) ?? [];
      const text = answer?.text?.trim();
      if (text) values.push(text);
      return `${question.label}: ${values.join('; ')}`;
    })
  ].join('\n');
}

export function AgentClarificationQuestions({
  group,
  disabled = false,
  onSubmit
}: AgentClarificationQuestionsProps) {
  const [drafts, setDrafts] = React.useState<Record<string, AnswerDraft>>(() => initialDrafts(group));
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    setDrafts(initialDrafts(group));
    setErrors({});
  }, [group.id]);

  const updateOptions = (questionId: string, optionId: string, multiple: boolean) => {
    setDrafts((current) => {
      const draft = current[questionId] ?? { optionIds: [], text: '' };
      const optionIds = multiple
        ? draft.optionIds.includes(optionId)
          ? draft.optionIds.filter((id) => id !== optionId)
          : [...draft.optionIds, optionId]
        : [optionId];
      return { ...current, [questionId]: { ...draft, optionIds } };
    });
    setErrors((current) => ({ ...current, [questionId]: '' }));
  };

  const updateText = (questionId: string, text: string) => {
    setDrafts((current) => ({
      ...current,
      [questionId]: { ...(current[questionId] ?? { optionIds: [] }), text }
    }));
    setErrors((current) => ({ ...current, [questionId]: '' }));
  };

  const submit = () => {
    const nextErrors: Record<string, string> = {};
    const answers = group.questions.map((question) => {
      const draft = drafts[question.id] ?? { optionIds: [], text: '' };
      const text = draft.text.trim();
      if (draft.optionIds.length === 0 && text.length === 0) {
        nextErrors[question.id] = 'Choose an option or enter an answer.';
      }
      return {
        questionId: question.id,
        optionIds: draft.optionIds,
        ...(text ? { text } : {})
      };
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    const bundle = { groupId: group.id, answers };
    onSubmit(bundle, formatClarificationAnswerSummary(group, bundle));
  };

  return (
    <section className="mt-3 space-y-4 rounded-md border bg-background p-3" aria-label="Agent clarification questions">
      <p className="text-xs text-muted-foreground">Review all answers, then send them together. Choosing an option does not send anything.</p>
      {group.questions.map((question, questionIndex) => {
        const draft = drafts[question.id] ?? { optionIds: [], text: '' };
        const errorId = `${group.id}-${question.id}-error`;
        const inputId = `${group.id}-${question.id}-text`;
        return (
          <fieldset
            key={question.id}
            className="min-w-0 space-y-2"
            aria-describedby={errors[question.id] ? errorId : undefined}
          >
            <legend className="text-sm font-medium">
              <span className="mr-1 text-muted-foreground">{questionIndex + 1}.</span>
              {question.label}
            </legend>
            {question.options.length > 0 ? (
              <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
                {question.options.map((option) => {
                  const checked = draft.optionIds.includes(option.id);
                  const inputType = question.mode === 'multiple_choice' ? 'checkbox' : 'radio';
                  return (
                    <label
                      key={option.id}
                      className={cn(
                        'flex min-w-0 cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm transition-colors hover:bg-muted/60',
                        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                        checked && 'border-foreground bg-muted',
                        disabled && 'cursor-not-allowed opacity-60'
                      )}
                    >
                      <input
                        type={inputType}
                        name={`${group.id}-${question.id}`}
                        value={option.id}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => updateOptions(question.id, option.id, question.mode === 'multiple_choice')}
                        className="mt-0.5 size-4 shrink-0 accent-foreground focus-visible:outline-none"
                      />
                      <span className="min-w-0">
                        <span className="block font-medium leading-5">{option.label}</span>
                        {option.description ? <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{option.description}</span> : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : null}
            {question.allowFreeText ? (
              <div className="space-y-1.5">
                <Label htmlFor={inputId} className="text-xs">
                  {question.mode === 'text' ? 'Your answer' : 'Add details or another answer'}
                </Label>
                <Textarea
                  id={inputId}
                  aria-label={`${question.mode === 'text' ? 'Your answer' : 'Additional details'}: ${question.label}`}
                  value={draft.text}
                  onChange={(event) => updateText(question.id, event.target.value)}
                  maxLength={1000}
                  rows={question.mode === 'text' ? 3 : 2}
                  disabled={disabled}
                  aria-invalid={Boolean(errors[question.id])}
                  className="min-h-16 resize-y"
                />
              </div>
            ) : null}
            {errors[question.id] ? <p id={errorId} className="text-xs text-destructive" role="alert">{errors[question.id]}</p> : null}
          </fieldset>
        );
      })}
      <Button type="button" size="sm" disabled={disabled} onClick={submit}>
        <SendIcon className="size-3.5" />
        Send answers
      </Button>
      <p className="text-[11px] text-muted-foreground">Sending answers supplies information only. It does not approve or execute a proposed action.</p>
    </section>
  );
}
